import type { Space } from '@mail/cross-sizes-utils';
import discountBadge from 'img/vigbo/vigbo-whats-new-discount-badge.png';
import React from 'react';
import { getPriceByPeriod } from 'reactApp/modules/vigbo/vigbo.helpers';
import type { Product } from 'reactApp/types/Billing';
import { TariffButton } from 'reactApp/ui/VigboWhatsNew/TariffButton/TariffButton';
import { TariffSpace } from 'reactApp/ui/VigboWhatsNew/TariffSpace/TariffSpace';
import { VigboFeature } from 'reactApp/ui/VigboWhatsNew/VigboFeature/VigboFeature';
import type { VigboWhatsNewProps } from 'reactApp/ui/VigboWhatsNew/VigboWhatsNew';
import { formatExpiresDayPointMonth, formatExpiresDaySpaceMonth } from 'reactApp/utils/formatDate';
import { getTypeOfPeriodName, PERIODS } from 'reactApp/utils/Period';

import styles from './VigboWhatsNew.css';

export const enum VigboWhatsNewType {
    renewRemind = 'renewRemind',
    endTariff = 'endTariff',
}

/** Содержит функции для получения пропсов разных типов модалок на основе различных данных */
export const VigboWhatsNewPropsBuilder = {
    [VigboWhatsNewType.renewRemind]: (
        expireDate: number,
        tariffSpace: Space,
        onClick: () => void,
        onClose: () => void,
        onShow: () => void
    ): VigboWhatsNewProps => {
        const titleDate = formatExpiresDaySpaceMonth(expireDate);
        const title = (
            <>
                <span className={styles.titleDate}>{titleDate}</span> у вас отключится подписка для фотографов Mail Space и Vigbo
            </>
        );
        const text = `Включите автопродление — и продолжайте создавать галереи Vigbo, заполнять ${tariffSpace.value} памяти и ретушировать фото сериями`;
        const bttnText = 'Включить автопродление';

        const date = formatExpiresDayPointMonth(expireDate);
        const additionalContent = (
            <div className={styles.renewRemind}>
                <div className={styles.expireDate}>Ваш тариф действует до {date}</div>
                <TariffSpace tariffSpace={tariffSpace} />
                <VigboFeature>+ Галереи Vigbo</VigboFeature>
                <VigboFeature>+ Ретушь</VigboFeature>
            </div>
        );

        return {
            title,
            text,
            additionalContent,
            bttnText,
            onClick,
            onClose,
            onShow,
        };
    },
    [VigboWhatsNewType.endTariff]: (
        product: Product,
        isOverquta: boolean,
        onClick: () => void,
        onClose: () => void,
        onShow: () => void
    ): VigboWhatsNewProps => {
        const title = 'Ваша подписка для фотографов Mail Space и Vigbo закончилась';
        const text = isOverquta
            ? `Пространство переполнено — скоро не получится смотреть и делиться. Подключите подписку, чтобы вернуть ${product.space.value} и галереи Vigbo`
            : `Подключите её снова, чтобы вернуть ${product.space.value} памяти и продолжить создавать галереи Vigbo`;

        const bttnText = 'Подключить со скидкой';

        const oldPrice = product.discountPrice && product.price;
        const oldPriceInMonthByPeriod = oldPrice && getPriceByPeriod(oldPrice, product.period);

        const price = product.discountPrice || product.price;
        const priceInMonthByPeriod = getPriceByPeriod(price, product.period);

        const periodName = getTypeOfPeriodName(product.period);
        const subtext = product.period !== PERIODS.month && periodName !== '' ? `при покупке на ${periodName}` : '';

        const additionalContent = (
            <div className={styles.endTariff}>
                <TariffSpace className={styles.tariffSpace} tariffSpace={product.space} />
                <VigboFeature>+ Галереи Vigbo</VigboFeature>
                <VigboFeature>+ Ретушь</VigboFeature>
                <TariffButton
                    onClick={onClick}
                    className={styles.tariffBttn}
                    text={
                        <>
                            {oldPriceInMonthByPeriod && (
                                <>
                                    <span className={styles.tariffOldPrice}>{oldPriceInMonthByPeriod}</span>&nbsp;
                                </>
                            )}
                            {priceInMonthByPeriod} ₽ в месяц
                        </>
                    }
                    subtext={subtext}
                />
                <img className={styles.discountBadge} src={discountBadge} />
            </div>
        );

        return {
            title,
            text,
            bttnText,
            additionalContent,
            onClick,
            onClose,
            onShow,
        };
    },
} as const;
